import type { ContactViewWire } from '@badger-sentinel/protocol';

import { detectionLadder } from '../model/selection.js';

/**
 * The detection ladder (design 1303–1311).
 *
 * Four rungs — `HIDDEN · FOUND · FIXED · TRACKED` (Q5, `DETECT` deleted) — of
 * which a contact can only ever be in three. `HIDDEN` is drawn as the floor this
 * contact has **already climbed off**, never as a state a row is in, because a
 * hidden contact is absent from the projection entirely. It is on the ladder
 * because a ladder starting at FOUND does not say what FOUND is better than.
 *
 * `title` on the whole strip rather than a caption under it: the ladder is
 * self-evident from its shape, and a sentence explaining that the filled rungs
 * are the ones reached is the mechanism narration the copy rules forbid.
 */
export function DetectionLadder(props: { readonly rung: ContactViewWire['rung'] }): JSX.Element {
  const steps = detectionLadder(props.rung);
  return (
    <div
      data-testid="detection-ladder"
      data-rung={props.rung}
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        padding: '12px 14px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-data)',
          fontSize: 11,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        TRACK
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          height: 24,
          border: '1px solid var(--border)',
        }}
      >
        {steps.map((step) => (
          <span
            key={step.rung}
            data-testid="ladder-rung"
            data-rung={step.rung}
            data-state={step.state}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: TONE[step.state].bg,
              borderRight: '1px solid var(--border)',
              fontFamily: 'var(--font-data)',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '.08em',
              color: TONE[step.state].color,
            }}
          >
            {step.rung}
          </span>
        ))}
      </div>
    </div>
  );
}

const TONE = Object.freeze({
  // The rungs a contact is not on are still four words the operator reads.
  PASSED: { bg: 'var(--surface-4)', color: 'var(--text-dim)' },
  // The rung a contact is on is a reading, not an act. Amber here put the loudest
  // colour in the system on a fact the operator cannot change from this panel.
  CURRENT: { bg: 'var(--accent)', color: 'var(--accent-on)' },
  AHEAD: { bg: 'transparent', color: 'var(--text-dim)' },
});
